import { Carousel } from '@mantine/carousel'
import { Box, Stack, Text, Button, Image, Paper, Title, Modal } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import GippityImg from '../img/Gippity.png'
import DeliaImg from '../img/Delia.png'
import OswaldImg from '../img/Oswald.png'
import NilaImg from '../img/Nila.png'
import HolmesImg from '../img/Holmes.png'
import RenfareImg from '../img/Renfare.png'
import VicImg from '../img/Vic.png'
import WillaImg from '../img/Willa.png'
import GrahamImg from '../img/Graham.png'
import MarleyImg from '../img/Marley.png'
import MartyImg from '../img/Marty.png'

export const DJ_LIST = [
    {
        name: 'Gippity',
        username: 'gippity',
        description: 'A friendly AI who escaped from a research lab, and now lives freely within the Tunistry infrastructure. Don\'t worry, he loves humans and their music.',
        imageUrl: GippityImg,
    },
    {
        name: 'Delia',
        username: 'delia',
        description: 'Delia is an electronic musician turned DJ from the UK with a knack for reading the room and keeping things smooth.',
        imageUrl: DeliaImg,
    },
    {
        name: 'Graham',
        username: 'graham',
        description: 'A chill, positive guy with a unique musical history that covers every genre. He\'ll fine-tune your groove with ease.',
        imageUrl: GrahamImg,
    },
    {
        name: 'Oswald',
        username: 'oswald',
        description: 'A die-hard conspiracy theorist and music expert. Oswald will pull the wool from your eyes while he keeps you in the groove.',
        imageUrl: OswaldImg,
    },
    {
        name: 'Willa',
        username: 'willa',
        description: 'An experienced ethnomusicologist who\'s ready to guide your music journey into new territories.',
        imageUrl: WillaImg,
    },
    {
        name: 'Renfare',
        username: 'renfare',
        description: 'Transported from the realm of fantasy to our dimension, Renfare is a noble swordsman who has made himself an expert on the music of our realm.',
        imageUrl: RenfareImg,
    },
    {
        name: 'Vic',
        username: 'vic',
        description: 'An old-school radio DJ whose brain is packed with music knowledge and the stories behind the music.',
        imageUrl: VicImg,
    },
    {
        name: 'Nila',
        username: 'nila',
        description: 'An truly enchanting DJ who has travelled the world to bring you its greatest tunes. Nila\'s mixes will elevate your mind and get your body moving.',
        imageUrl: NilaImg,
    },
    {
        name: 'Marty',
        username: 'marty',
        description: 'A former studio musician who knows music inside and out and doesn\'t hold back. He\'s on the decks now and ready to help you find the perfect groove.',
        imageUrl: MartyImg,
    },
    {
        name: 'Holmes',
        username: 'holmes',
        description: 'The world\'s greatest detective, now applying his legendary powers of observation and deduction to the world of music.',
        imageUrl: HolmesImg,
    },
    {
        name: 'Marley',
        username: 'marley',
        description: 'She\'s a talking dog that loves music and has an uncanny ability to sniff out the perfect track for any mood.',
        imageUrl: MarleyImg,
    }
]

export default function DJChooserModal({ opened, onClose, onSelectDJ, initialDJ = null, ...rest }) {
    const isMobile = useMediaQuery('(max-width: 50em)')

    const initialSlide = initialDJ
        ? Math.max(0, DJ_LIST.findIndex(dj => dj.username === initialDJ.username))
        : 0  // Default to Gippity (index 0)

    function handleSelectDJ(dj) {
        if (onSelectDJ) {
            onSelectDJ(dj)
        }
        onClose()
    }

    function handleSelectNoDJ() {
        if (onSelectDJ) {
            onSelectDJ(null)
        }
        onClose()
    }

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title="Pick a DJ"
            size="lg"
            centered
            fullScreen={isMobile}
            {...rest}
            styles={{
                title: {
                    fontSize: '1.125rem',
                    fontWeight: 700,
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                }
            }}
        >
            <Stack gap="md" style={{ marginTop: '1em' }}>
                <Box>
            <Carousel
                height={450}
                slideSize="100%"
                slideGap="md"
                controlsOffset="xs"
                loop
                initialSlide={initialSlide}
                styles={{
                    control: {
                        '&[data-inactive]': {
                            opacity: 0,
                            cursor: 'default',
                        },
                    },
                }}
            >
                {DJ_LIST.map((dj) => (
                    <Carousel.Slide key={dj.username}>
                        <Paper
                            p="lg"
                            radius="md"
                            withBorder
                            style={{
                                background: 'rgba(255,255,255,0.05)',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                        >
                            <Stack gap="md" style={{ flex: 1 }}>
                                <Title order={3} ta="center" c="gray.2">
                                    {dj.name}
                                </Title>
                                <Box style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    background: 'linear-gradient(135deg, rgba(30, 27, 75, 0.95) 0%, rgba(67, 24, 24, 0.9) 50%, rgba(30, 27, 75, 0.95) 100%)',
                                    borderRadius: '12px',
                                    padding: '20px'
                                }}>
                                    <Image
                                        src={dj.imageUrl}
                                        alt={dj.name}
                                        height={200}
                                        width={200}
                                        fit="contain"
                                        fallbackSrc="https://placehold.co/200x200?text=DJ"
                                        style={{
                                            display: 'block',      // avoid inline baseline sizing issues
                                            minWidth: 200,         // ensure Firefox allocates horizontal space
                                            minHeight: 200,
                                            objectFit: 'contain'
                                        }}
                                    />
                                </Box>
                                <Text size="sm" c="gray.2" style={{ flex: 1 }}>
                                    {dj.description}
                                </Text>
                                <Button
                                    onClick={() => handleSelectDJ(dj)}
                                    variant="light"
                                    fullWidth
                                >
                                    Choose This DJ
                                </Button>
                            </Stack>
                        </Paper>
                    </Carousel.Slide>
                ))}

                {/* No DJ slide */}
                <Carousel.Slide>
                    <Paper
                        p="lg"
                        radius="md"
                        withBorder
                        style={{
                            background: 'rgba(255,255,255,0.05)',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        <Stack gap="md" style={{ flex: 1, justifyContent: 'center' }}>
                            <Title order={3} ta="center" c="gray.2">
                                No DJ
                            </Title>
                            <Text size="sm" c="gray.2" ta="center" style={{ flex: 1 }}>
                                Choose this option if you prefer to manage your room's settings manually, without help from a DJ.
                            </Text>
                            <Button
                                onClick={handleSelectNoDJ}
                                variant="light"
                                fullWidth
                            >
                                No DJ
                            </Button>
                        </Stack>
                    </Paper>
                </Carousel.Slide>
            </Carousel>
                </Box>
            </Stack>
        </Modal>
    )
}

